import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import * as routes from 'features/Bank/routes';

interface ReadOnlyURLSearchParams extends URLSearchParams {
  append: never;
  set: never;
  delete: never;
  sort: never;
}

const getBankOverviewPath = (params: URLSearchParams) => {
  return `${routes.bankOverview}?${params.toString()}`;
};

/**
 * Hook to manipulate the search parameters used to toggle UI widgets on the overview page and
 * build the paths for those while preserving other search parameters (e.g., transaction filters).
 */
export const useBankOverviewSearchParams = () => {
  const { featureFlags } = useFeatureFlagsSwitcher();
  const { bankAccounts, loading: loadingBankAccounts } =
    useCompanyBankAccounts();
  const history = useHistory();
  const { search } = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(search) as ReadOnlyURLSearchParams,
    [search],
  );

  const currentBankAccountId = searchParams.get('bankAccountId') ?? undefined;
  const currentTransactionId = searchParams.get('transactionId') ?? undefined;
  const currentReceiptId = searchParams.get('receiptId') ?? undefined;
  const showReceipt = Boolean(searchParams.get('showReceipt'));
  const showHelp = Boolean(searchParams.get('showHelp'));

  const getSearchParams = useCallback(() => {
    return new URLSearchParams(searchParams.toString());
  }, [searchParams]);

  const getRootPath = useCallback(() => {
    const newSearchParams = getSearchParams();

    newSearchParams.delete('showReceipt');
    newSearchParams.delete('transactionId');
    newSearchParams.delete('showHelp');

    return getBankOverviewPath(newSearchParams);
  }, [getSearchParams]);

  const getTransactionPath = useCallback(
    (transactionId: string) => {
      const newSearchParams = getSearchParams();

      newSearchParams.delete('showReceipt');
      newSearchParams.delete('receiptId');
      newSearchParams.delete('showHelp');
      newSearchParams.set('transactionId', transactionId);

      return getBankOverviewPath(newSearchParams);
    },
    [getSearchParams],
  );

  const getTransactionHelpPath = useCallback(
    (transactionId: string) => {
      const newSearchParams = getSearchParams();

      newSearchParams.set('transactionId', transactionId);
      newSearchParams.set('showHelp', 'true');

      return getBankOverviewPath(newSearchParams);
    },
    [getSearchParams],
  );

  const getTransactionReceiptSearch = useCallback(
    (transactionId: string, receiptId?: string) => {
      const newSearchParams = getSearchParams();

      newSearchParams.set('transactionId', transactionId);

      /**
       * @todo Remove showReceipt logic and rely entirely on receiptId after multi-doc feature release
       * @link https://linear.app/shine/issue/CAC-58
       */
      if (featureFlags.enableMultipleTransactionDocs && receiptId) {
        newSearchParams.set('receiptId', receiptId);
      } else {
        newSearchParams.set('showReceipt', 'true');
      }

      return newSearchParams.toString();
    },
    [getSearchParams, featureFlags.enableMultipleTransactionDocs],
  );

  const setBankAccountId = useCallback(
    (bankAccountId: string | undefined) => {
      const newSearchParams = getSearchParams();

      if (bankAccountId) {
        newSearchParams.set('bankAccountId', bankAccountId);
      } else {
        newSearchParams.delete('bankAccountId');
      }

      history.replace({
        search: `?${newSearchParams.toString()}`,
      });
    },
    [history, getSearchParams],
  );

  // If the current bankAccountId does not match a company bank account id,
  // remove it from the search params.
  useLayoutEffect(() => {
    if (!currentBankAccountId || loadingBankAccounts) {
      return;
    }
    if (
      !bankAccounts.some(
        ({ bankAccountId }) => bankAccountId === currentBankAccountId,
      )
    ) {
      setBankAccountId(undefined);
    }
  }, [
    bankAccounts,
    currentBankAccountId,
    loadingBankAccounts,
    setBankAccountId,
  ]);

  return {
    bankAccountId: currentBankAccountId,
    getRootPath,
    getTransactionHelpPath,
    getTransactionPath,
    getTransactionReceiptSearch,
    receiptId: currentReceiptId,
    setBankAccountId,
    showHelp,
    showReceipt,
    transactionId: currentTransactionId,
  };
};
